import { CHESS_MODEL_STRATEGY, DEFAULT_CHESS_MODEL_PARAMETERS } from './chessProbability';
import { buildBoardProbabilities } from './chessTeamMatch';
import { OLYMPIAD_MODEL_MINUTES, normalizeBoardResult, isForfeitResult, boardScoresFromResult } from './chessOlympiadResults';

export const OLYMPIAD_PERFORMANCE_K = 10;
// One record per published board, always in WHITE / DRAW / BLACK order.
// No assumed lineups and no result-conditioned probabilities enter this ledger.
export function olympiadPerformanceRecords(matches, teams, modelStrategy, strengthReference) {
  const players = new Map(teams.flatMap(team => (team.roster || []).map(player => [player.player_id, player])));
  return matches.flatMap(match => (match.boards || []).map((board, index) => {
    const white = players.get(board.white_player_id), black = players.get(board.black_player_id);
    const result = normalizeBoardResult(board.result);
    const forfeit = isForfeitResult(result);
    const saved = modelStrategy === CHESS_MODEL_STRATEGY ? match.prematch?.boards?.[index] : null;
    let probabilities = null;
    if (saved) {
      const aWhite = saved.teamAColor ? saved.teamAColor === 'white' : (index % 2 === 0) === (match.team_a_colour !== 'black');
      probabilities = [aWhite ? saved.teamAWin : saved.teamBWin, saved.draw, aWhite ? saved.teamBWin : saved.teamAWin];
    } else if (white && black && !forfeit) {
      const player = p => ({id:p.player_id, elo:Number(p.rating_used), drawFactor:Number(p.individual_draw_factor ?? 1)});
      const p = buildBoardProbabilities({teamAPlayer:player(white), teamBPlayer:player(black), teamAColor:'white', effectiveMinutes:OLYMPIAD_MODEL_MINUTES, parameters:DEFAULT_CHESS_MODEL_PARAMETERS, modelStrategy, strengthReference});
      probabilities = [p.teamAWin,p.draw,p.teamBWin];
    }
    if (probabilities && (probabilities.some(p => !Number.isFinite(p) || p < 0 || p > 1) || Math.abs(probabilities.reduce((a,b)=>a+b,0)-1)>1e-10)) probabilities = null;
    const outcome = ['1-0','1/2-1/2','0-1'].indexOf(result);
    return {round:Number(match.round), matchId:match.id, board:index+1, whiteId:board.white_player_id, blackId:board.black_player_id,
      whiteName:white?.name_used, blackName:black?.name_used, result, forfeit, probabilities, outcome,
      reconstructed:!saved || match.prematch?.basis === 'reconstructed'};
  }));
}

export function summarizeOlympiadPerformance(records) {
  const stats = {games:0, finished:0, expected:[0,0,0], actual:[0,0,0], expectedSum:0, actualSum:0, open:0, forfeits:0, unavailable:0, reconstructed:0};
  for (const record of records) {
    if (record.forfeit) {stats.forfeits++; continue;}
    if (![0,1,2].includes(record.outcome)) {stats.open++; continue;}
    if (!record.probabilities) {stats.unavailable++; continue;}
    stats.games++;
    if (record.reconstructed) stats.reconstructed++;
    const squared = record.probabilities.reduce((sum,p)=>sum+p*p,0);
    stats.expectedSum += squared;
    record.probabilities.forEach((p,i)=>{stats.expected[i]+=p;});
    if (record.outcome >= 0) {
      stats.finished++;
      stats.actual[record.outcome]++;
      stats.actualSum+=record.probabilities[record.outcome];

    }
  }
  return {...stats, expectedAverage:stats.games ? stats.expectedSum/stats.games : null,
    actualAverage:stats.games ? stats.actualSum/stats.games : null};
}

export function olympiadPlayerPerformance(records, playerIds) {
  const ids = new Set(playerIds);
  const stats = {games:0, score:0, expected:0, playedGames:0, playedScore:0, wins:0, losses:0, rounds:{}};
  for (const record of records) {
    for (const [id,white] of [[record.whiteId,true],[record.blackId,false]]) {
      if (!ids.has(id)) continue;
      const scores = record.result === '--' ? [0,0] : ['1-0','+-'].includes(record.result) ? [1,0] : ['0-1','-+'].includes(record.result) ? [0,1] : record.result === '1/2-1/2' ? [0.5,0.5] : null;
      const score = scores ? scores[white ? 0 : 1] : null;
      const expected = record.probabilities ? record.probabilities[white ? 0 : 2]+record.probabilities[1]/2 : null;
      const entry = {score, expected, forfeit:record.forfeit, opponent:white ? record.blackName : record.whiteName, colour:white ? 'Weiß' : 'Schwarz'};
      (stats.rounds[record.round] ||= []).push(entry);
      if (record.forfeit || score == null) continue;
      stats.playedGames++;stats.playedScore+=score;
      if (score === 1) stats.wins++;
      if (score === 0) stats.losses++;
      if (expected == null) continue;
      stats.games++;stats.score+=score;stats.expected+=expected;
    }
  }
  return {...stats, balance:stats.wins-stats.losses, eloDelta:OLYMPIAD_PERFORMANCE_K*(stats.score-stats.expected), percentagePointDelta:stats.games ? 100*(stats.score-stats.expected)/stats.games : null};
}

// Live board points are available before the entire team match is finished.
// Match points remain unawarded until all boards (or an official aggregate) are final.
export function olympiadLiveMatchScore(match) {
  const decided = (match.boards || []).map((board,index) => boardScoresFromResult(board.result,
    (index % 2 === 0) === (match.team_a_colour !== 'black') ? 'white' : 'black')).filter(Boolean);
  const completed = Boolean(match.completed) || decided.length === 4;
  const aggregateAvailable = match.team_a_score != null && match.team_b_score != null
    && Number.isFinite(Number(match.team_a_score)) && Number.isFinite(Number(match.team_b_score));
  const scores = completed && decided.length !== 4 && aggregateAvailable
    ? [Number(match.team_a_score), Number(match.team_b_score)]
    : decided.reduce((sum,board)=>[sum[0]+board[0],sum[1]+board[1]],[0,0]);
  return {scores, completed, hasResults:decided.length > 0 || (completed && aggregateAvailable),
    decidedBoards:decided.length, openBoards:completed ? 0 : Math.max(0,4-decided.length)};
}
